/**
 * Custom Hook for Toast Notifications
 * Provides a simple API for showing success, error, info, and warning toasts
 */

import { OptionsObject, useSnackbar, VariantType } from 'notistack';

export interface NotificationHook {
  success: (message: string, options?: OptionsObject) => void;
  error: (message: string, options?: OptionsObject) => void;
  info: (message: string, options?: OptionsObject) => void;
  warning: (message: string, options?: OptionsObject) => void;
  toast: (message: string, variant?: VariantType, options?: OptionsObject) => void;
}

/**
 * Hook to show toast notifications
 *
 * @example
 * ```tsx
 * const notification = useNotification();
 *
 * // Show success toast
 * notification.success('Data saved successfully!');
 *
 * // Show error toast
 * notification.error('Failed to save data');
 *
 * // Show info toast
 * notification.info('Processing your request...');
 *
 * // Show warning toast
 * notification.warning('This action cannot be undone');
 * ```
 */
export const useNotification = (): NotificationHook => {
  const { enqueueSnackbar } = useSnackbar();

  const success = (message: string, options?: OptionsObject) => {
    enqueueSnackbar(message, { ...options, variant: 'success' });
  };

  const error = (message: string, options?: OptionsObject) => {
    enqueueSnackbar(message, { ...options, variant: 'error' });
  };

  const info = (message: string, options?: OptionsObject) => {
    enqueueSnackbar(message, { ...options, variant: 'info' });
  };

  const warning = (message: string, options?: OptionsObject) => {
    enqueueSnackbar(message, { ...options, variant: 'warning' });
  };

  const toast = (message: string, variant: VariantType = 'default', options?: OptionsObject) => {
    enqueueSnackbar(message, { ...options, variant });
  };

  return {
    success,
    error,
    info,
    warning,
    toast,
  };
};
